import { useEffect } from 'react';

import { useAuth } from 'react-oidc-context';

const COOKIE_NAME = 'cuc-session';
const COOKIE_DOMAIN = '.cucollaborate.com';
const POLL_INTERVAL_MS = 2000;
// Tokens issued within this window are considered fresh (just-completed sign-in
// or silent renew). Older tokens are assumed to be rehydrated from storage and
// must not revive a cuc-session that was deliberately cleared by another app.
const FRESH_TOKEN_WINDOW_SECONDS = 30;

function setCookie(value: string): void {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
}

function clearCookie(): void {
  document.cookie = `${COOKIE_NAME}=; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function readCookie(): string | null {
  const match = new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`).exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Cross-tab logout sync across *.cucollaborate.com.
 *
 * Sets a shared cookie at .cucollaborate.com keyed to the signed-in user's
 * sub. Polls every POLL_INTERVAL_MS — if the cookie disappears or no longer
 * matches, the local session is torn down so the user gets re-prompted.
 *
 * Cleared on local sign-out via the UserManager's userSignedOut / userUnloaded
 * events, so any sibling tab at another *.cucollaborate.com subdomain detects
 * the logout within ~2 seconds.
 */
export function useSessionSync(): void {
  const auth = useAuth();
  const sub = auth.user?.profile.sub;
  const iat = auth.user?.profile.iat;
  const expiresAt = auth.user?.expires_at;
  const isAuthed = auth.isAuthenticated;

  useEffect(() => {
    if (!isAuthed || !sub) return;
    if (expiresAt && expiresAt * 1000 <= Date.now()) return;
    const existing = readCookie();
    if (existing === sub) return;
    if (existing !== null) return;
    const tokenAge = iat ? Date.now() / 1000 - iat : Infinity;
    if (tokenAge > FRESH_TOKEN_WINDOW_SECONDS) return;
    setCookie(sub);
  }, [isAuthed, sub, iat, expiresAt]);

  useEffect(() => {
    const offSignedOut = auth.events.addUserSignedOut(clearCookie);
    const offUnloaded = auth.events.addUserUnloaded(clearCookie);
    return () => {
      offSignedOut();
      offUnloaded();
    };
  }, [auth.events]);

  useEffect(() => {
    if (!isAuthed || !sub) return;
    const id = window.setInterval(() => {
      if (readCookie() !== sub) {
        void auth.removeUser().finally(() => {
          window.location.reload();
        });
      }
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [isAuthed, sub, auth]);
}
